import Image from 'next/image';
import {
  ArrowUDownLeft,
  BookmarkSimple,
  Info,
  Notebook,
  Percent,
  RocketLaunch,
  Star,
  ThumbsUp,
} from 'phosphor-icons';
import xss from 'xss';
import { c } from '../../../utils/tailwind-merge';
import {
  NotificationItemProps,
  NotificationItemPropsType,
} from './NotificationItem.types';

const NOTIFICATION_ICON_MAP: Record<
  NotificationItemPropsType,
  React.ElementType
> = {
  'general-promotion': Percent,
  'asset-created': RocketLaunch,
  'asset-updated': RocketLaunch,
  'asset-on-sale': BookmarkSimple,
  'asset-onboarding': Notebook,
  'review-requested': Star,
  'review-replied': ArrowUDownLeft,
  'review-reacted': ThumbsUp,
  'refund-status-updated': Info,
};

export const NotificationItem = ({
  id,
  type,
  title,
  description,
  timeAgo,
  imageUrl,
  link,
  isRead,
  className = '',
  onNotificationItemClick,
}: NotificationItemProps) => {
  const IconComponent = NOTIFICATION_ICON_MAP[type];
  const ContainerElement = link ? 'a' : 'button';
  const containerElementSpecificProps = link
    ? { href: link, target: '_blank', rel: 'noopener noreferrer' }
    : ({ type: 'button' } as const);

  return (
    <ContainerElement
      {...containerElementSpecificProps}
      className={c(
        'relative flex w-full cursor-pointer flex-col gap-1 rounded-none border-b border-b-grey-800 bg-grey-900 px-4 py-3 text-left leading-5 text-grey-400 hover:bg-grey-800 focus:z-10 focus:outline focus:outline-2 focus:-outline-offset-4 focus:outline-blue',
        className
      )}
      onClick={() => onNotificationItemClick(id)}
    >
      <div className="flex items-center justify-between">
        <div className="ml-0.5 flex items-center gap-2">
          <IconComponent className="h-4 w-4 text-grey-400" />
          <span className="text-xs tracking-[-0.24px]">{title}</span>
        </div>
        {!isRead && <div className="h-1.5 w-1.5 rounded-full bg-blue" />}
      </div>

      <div className="flex w-full justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div
            className="w-fit text-sm tracking-[-0.084px] text-grey-100"
            dangerouslySetInnerHTML={{ __html: xss(description) }}
          />
          <div className="text-xs leading-5">{timeAgo}</div>
        </div>

        {imageUrl && (
          <div className="relative aspect-3/2 h-[42px] shrink-0">
            <Image
              fill
              alt=""
              aria-hidden={true}
              src={imageUrl}
              className="rounded-thumbs"
            />
          </div>
        )}
      </div>
    </ContainerElement>
  );
};
